import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

// Shared markdown + LaTeX renderer. Two variants:
//   - default: generous spacing for summaries, abstracts, long-form content.
//   - compact: tight spacing tuned for chat bubbles and card teasers.
// KaTeX CSS is loaded globally in src/index.js — math (`$...$`, `$$...$$`) is
// handled by remark-math + rehype-katex. HTML in the source is escaped by
// default (react-markdown is XSS-safe without rehype-raw).

const buildComponents = (compact) => ({
  h1: ({ children, ...props }) => (
    <h3
      className={
        compact
          ? 'text-sm font-bold text-gray-900 mt-3 mb-1.5 first:mt-0'
          : 'text-base font-bold text-gray-900 mt-4 mb-2 first:mt-0'
      }
      {...props}
    >
      {children}
    </h3>
  ),
  h2: ({ children, ...props }) => (
    <h3
      className={
        compact
          ? 'text-xs font-bold uppercase tracking-wider text-gray-900 mt-2.5 mb-1 first:mt-0'
          : 'text-sm font-bold text-gray-900 mt-4 mb-1.5 first:mt-0'
      }
      {...props}
    >
      {children}
    </h3>
  ),
  h3: ({ children, ...props }) => (
    <h4
      className={
        compact
          ? 'text-xs font-semibold text-gray-900 mt-2 mb-1 first:mt-0'
          : 'text-sm font-semibold text-gray-900 mt-3 mb-1 first:mt-0'
      }
      {...props}
    >
      {children}
    </h4>
  ),
  h4: ({ children, ...props }) => (
    <h5
      className={
        compact
          ? 'text-xs font-semibold text-gray-800 mt-2 mb-1 first:mt-0'
          : 'text-xs font-semibold uppercase tracking-wider text-gray-700 mt-2.5 mb-1 first:mt-0'
      }
      {...props}
    >
      {children}
    </h5>
  ),
  p: (props) => (
    <p
      className={
        compact ? 'mb-1.5 last:mb-0' : 'mb-2.5 last:mb-0 leading-relaxed'
      }
      {...props}
    />
  ),
  ul: (props) => (
    <ul
      className={
        compact
          ? 'list-disc pl-4 mb-1.5 last:mb-0 space-y-0.5'
          : 'list-disc pl-5 mb-2.5 last:mb-0 space-y-1'
      }
      {...props}
    />
  ),
  ol: (props) => (
    <ol
      className={
        compact
          ? 'list-decimal pl-4 mb-1.5 last:mb-0 space-y-0.5'
          : 'list-decimal pl-5 mb-2.5 last:mb-0 space-y-1'
      }
      {...props}
    />
  ),
  li: (props) => <li className="leading-relaxed" {...props} />,
  a: ({ children, ...props }) => (
    <a
      className="text-purple-600 underline underline-offset-2 hover:text-purple-800"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
  strong: (props) => (
    <strong className="font-semibold text-gray-900" {...props} />
  ),
  em: (props) => <em className="italic" {...props} />,
  blockquote: (props) => (
    <blockquote
      className="border-l-2 border-purple-200 pl-3 my-2 text-gray-600 italic"
      {...props}
    />
  ),
  hr: () => <hr className="my-3 border-gray-100" />,
  code: ({ className, children, ...props }) => {
    const isBlock = /language-/.test(className || '');
    if (isBlock) {
      return (
        <code
          className={`${className} block text-[0.85em] font-mono leading-relaxed`}
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className="px-1 py-0.5 bg-gray-100 rounded text-[0.9em] font-mono text-gray-800"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: (props) => (
    <pre
      className="mb-2.5 last:mb-0 p-3 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto"
      {...props}
    />
  ),
  table: (props) => (
    <div className="overflow-x-auto mb-2.5 last:mb-0">
      <table className="w-full text-left border-collapse text-[0.9em]" {...props} />
    </div>
  ),
  thead: (props) => <thead className="border-b border-gray-200" {...props} />,
  th: (props) => (
    <th className="px-2 py-1.5 font-semibold text-gray-900" {...props} />
  ),
  td: (props) => (
    <td className="px-2 py-1.5 border-b border-gray-100" {...props} />
  ),
});

const remarkPlugins = [remarkGfm, remarkMath];
const rehypePlugins = [rehypeKatex];

// LLMs (especially GPT) frequently emit math with bracket/paren delimiters
// instead of the `$...$` / `$$...$$` that remark-math expects. We normalize
// four common variants so math renders regardless of how the model decided
// to delimit it:
//   \[ ... \]     (escaped LaTeX block)      → $$...$$
//   \( ... \)     (escaped LaTeX inline)     → $...$
//   [ ... ]       (bracket block, ChatGPT)   → $$...$$   (when content looks TeX-y)
//   ( ... )       (paren inline, ChatGPT)    → $...$     (when content looks TeX-y)
// The looks-TeX-y check (a `\command` or `_` / `^`) is what keeps us from
// eating ordinary prose like "(see below)" or markdown links `[text](url)`.
const MATH_SEGMENT_RE = /(\$\$[\s\S]*?\$\$|\$[^$\n]+?\$)/g;
const PAREN_INLINE_MATH_RE =
  /\(((?:[^()\n]|\([^()\n]*\))*(?:\\[a-zA-Z]+|[_^])(?:[^()\n]|\([^()\n]*\))*)\)/g;

function normalizeMathDelimiters(text) {
  if (!text || typeof text !== 'string') return text;
  let out = text;

  // Escaped-bracket variants first — always safe to convert since these
  // delimiters are unambiguous.
  out = out.replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$');
  out = out.replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$');

  // Bracket block math: `[ <stuff with backslash-command or '='> ]`, not
  // followed by `(` so we don't clobber markdown links. Require surrounding
  // whitespace inside the brackets to further disambiguate from link text.
  out = out.replace(
    /\[\s+([^[\]]*(?:\\[a-zA-Z]+|=)[^[\]]*)\s+\](?!\()/g,
    '$$$$$1$$$$'
  );

  // Paren inline math. Applied only outside existing $-delimited regions so
  // we don't mangle constructs like `\left(\frac{x}{y}\right)` that already
  // live inside a math block. Content allows one level of nested parens so
  // `(O(N^2 d))` collapses to `$O(N^2 d)$` in a single pass. Gated on a TeX
  // command, `_`, or `^` to avoid eating ordinary parenthetical prose.
  const parts = [];
  let lastIndex = 0;
  let m;
  MATH_SEGMENT_RE.lastIndex = 0;
  while ((m = MATH_SEGMENT_RE.exec(out)) !== null) {
    if (m.index > lastIndex) {
      parts.push(
        out.slice(lastIndex, m.index).replace(PAREN_INLINE_MATH_RE, '$$$1$$')
      );
    }
    parts.push(m[0]);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < out.length) {
    parts.push(out.slice(lastIndex).replace(PAREN_INLINE_MATH_RE, '$$$1$$'));
  }
  return parts.join('');
}

const MarkdownRenderer = ({ content, compact = false, className = '' }) => {
  const components = useMemo(() => buildComponents(compact), [compact]);
  const normalized = useMemo(() => normalizeMathDelimiters(content), [content]);
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {normalized || ''}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
